import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrandedLoading, BrandedSpinner } from '@/components/common/BrandedLoading';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Label } from '@/components/ui/label';
import {
  Bell,
  BellOff,
  Mail,
  Smartphone,
  MessageCircle,
  Shield,
  BookOpen,
  CreditCard,
  Megaphone,
  Settings,
  Save,
  AlertTriangle,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createApiService } from '@/services/api';
import { useToast } from '@/hooks/useToast';

// ──────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────

interface NotificationPreference {
  id?: string;
  userId: string;
  preferences: Record<string, Record<string, boolean>>;
  globalUnsubscribe: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  errorCode?: string;
  timestamp?: string;
}

// ──────────────────────────────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────────────────────────────

const CHANNELS = [
  { key: 'EMAIL', label: 'Email', icon: Mail },
  { key: 'SMS', label: 'SMS', icon: Smartphone },
  { key: 'PUSH_NOTIFICATION', label: 'Push', icon: Bell },
  { key: 'WHATSAPP', label: 'WhatsApp', icon: MessageCircle },
] as const;

const CATEGORIES = [
  {
    key: 'BILLING',
    label: 'Billing',
    description: 'Payment confirmations, invoices, and subscription updates',
    icon: CreditCard,
  },
  {
    key: 'SECURITY',
    label: 'Security',
    description: 'Login alerts, password changes, and security warnings',
    icon: Shield,
  },
  {
    key: 'LEARNING',
    label: 'Learning',
    description: 'Course updates, progress reminders, and achievements',
    icon: BookOpen,
  },
  {
    key: 'MARKETING',
    label: 'Marketing',
    description: 'Promotions, newsletters, and product announcements',
    icon: Megaphone,
  },
  {
    key: 'SYSTEM',
    label: 'System',
    description: 'Maintenance notices, policy updates, and platform news',
    icon: Settings,
  },
] as const;

// ──────────────────────────────────────────────────────────────────────
// API
// ──────────────────────────────────────────────────────────────────────

const NOTIFICATION_API_BASE_URL =
  import.meta.env.VITE_NOTIFICATION_API_BASE_URL || '/api/notification';

const notificationApi = createApiService(NOTIFICATION_API_BASE_URL);

const unwrapResponse = <T,>(response: ApiResponse<T> | T): T => {
  if (response && typeof response === 'object' && 'success' in response) {
    const wrapped = response as ApiResponse<T>;
    if (!wrapped.success || wrapped.data === undefined) {
      throw new Error(wrapped.message || 'API request failed');
    }
    return wrapped.data;
  }
  return response as T;
};

const preferencesApi = {
  get: async (userId: string): Promise<NotificationPreference> => {
    const res = await notificationApi.get<ApiResponse<NotificationPreference>>(
      `/api/v1/notification-preferences/user/${userId}`
    );
    return unwrapResponse(res);
  },

  update: async (
    userId: string,
    preferences: Record<string, Record<string, boolean>>
  ): Promise<NotificationPreference> => {
    const res = await notificationApi.put<ApiResponse<NotificationPreference>>(
      `/api/v1/notification-preferences/user/${userId}`,
      preferences
    );
    return unwrapResponse(res);
  },

  unsubscribe: async (userId: string): Promise<NotificationPreference> => {
    const res = await notificationApi.post<ApiResponse<NotificationPreference>>(
      `/api/v1/notification-preferences/user/${userId}/unsubscribe`
    );
    return unwrapResponse(res);
  },

  resubscribe: async (userId: string): Promise<NotificationPreference> => {
    const res = await notificationApi.post<ApiResponse<NotificationPreference>>(
      `/api/v1/notification-preferences/user/${userId}/resubscribe`
    );
    return unwrapResponse(res);
  },
};

// ──────────────────────────────────────────────────────────────────────
// Toggle Switch component (animated)
// ──────────────────────────────────────────────────────────────────────

interface ToggleSwitchProps {
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  id?: string;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  checked,
  onChange,
  disabled = false,
  id,
}) => {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`
        relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full
        border-2 border-transparent transition-colors duration-200 ease-in-out
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
        disabled:cursor-not-allowed disabled:opacity-50
        ${checked ? 'bg-primary' : 'bg-muted'}
      `}
    >
      <motion.span
        layout
        transition={{ type: 'spring', stiffness: 700, damping: 30 }}
        className={`
          pointer-events-none inline-block h-4 w-4 rounded-full bg-background shadow-lg ring-0
          ${checked ? 'translate-x-5' : 'translate-x-0.5'}
        `}
      />
    </button>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Page Component
// ──────────────────────────────────────────────────────────────────────

export const NotificationPreferencesPage: React.FC = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { toast } = useToast();

  const [preferences, setPreferences] = useState<Record<string, Record<string, boolean>>>({});
  const [globalUnsubscribe, setGlobalUnsubscribe] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const userId = user?.id ?? '';

  // ── Fetch preferences ─────────────────────────────────────────────
  const fetchPreferences = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await preferencesApi.get(userId);
      setPreferences(data.preferences ?? {});
      setGlobalUnsubscribe(data.globalUnsubscribe ?? false);
      setDirty(false);
    } catch (err) {
      console.error('[NotificationPreferencesPage] Failed to load preferences:', err);
      // Build defaults client-side when the server returns nothing
      const defaults: Record<string, Record<string, boolean>> = {};
      for (const ch of CHANNELS) {
        defaults[ch.key] = {};
        for (const cat of CATEGORIES) {
          defaults[ch.key][cat.key] = cat.key !== 'MARKETING';
        }
      }
      setPreferences(defaults);
      setGlobalUnsubscribe(false);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isAuthenticated && userId) {
      fetchPreferences();
    }
  }, [isAuthenticated, userId, fetchPreferences]);

  // ── Handlers ──────────────────────────────────────────────────────
  const togglePreference = (channel: string, category: string) => {
    setPreferences((prev) => {
      const updated = { ...prev };
      if (!updated[channel]) updated[channel] = {};
      updated[channel] = {
        ...updated[channel],
        [category]: !updated[channel][category],
      };
      return updated;
    });
    setDirty(true);
  };

  const handleGlobalUnsubscribeToggle = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      if (!globalUnsubscribe) {
        // Turning on global unsubscribe
        const data = await preferencesApi.unsubscribe(userId);
        setGlobalUnsubscribe(data.globalUnsubscribe);
        toast({ title: 'Unsubscribed', description: 'You have been unsubscribed from all notifications.' });
      } else {
        // Turning off global unsubscribe
        const data = await preferencesApi.resubscribe(userId);
        setGlobalUnsubscribe(data.globalUnsubscribe);
        toast({ title: 'Resubscribed', description: 'Your individual notification preferences are now active.' });
      }
    } catch (err) {
      console.error('[NotificationPreferencesPage] Global unsubscribe toggle failed:', err);
      toast({
        title: 'Error',
        description: 'Failed to update subscription status. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      await preferencesApi.update(userId, preferences);
      setDirty(false);
      toast({ title: 'Saved', description: 'Your notification preferences have been updated.' });
    } catch (err) {
      console.error('[NotificationPreferencesPage] Failed to save preferences:', err);
      toast({
        title: 'Error',
        description: 'Failed to save preferences. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Loading / Auth gate ───────────────────────────────────────────
  if (authLoading || loading) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <main className="flex flex-1 items-center justify-center px-4">
          <BrandedLoading
            message={authLoading ? 'Completing authentication…' : 'Loading notification preferences…'}
            iconSize="md"
          />
        </main>
        <Footer />
      </div>
    );
  }

  if (!isAuthenticated || !userId) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <main className="flex flex-1 items-center justify-center p-6">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>Sign In Required</CardTitle>
              <CardDescription>
                Please sign in to manage your notification preferences.
              </CardDescription>
            </CardHeader>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />

      <main id="main-content" className="flex-1">
        <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
          {/* Page heading */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mb-8"
          >
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              Notification Preferences
            </h1>
            <p className="mt-2 text-muted-foreground">
              Choose how and when you want to hear from us. Your changes are saved
              when you press <strong>Save Preferences</strong>.
            </p>
          </motion.div>

          {/* Global unsubscribe card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.05 }}
            className="mb-6"
          >
            <Card className={globalUnsubscribe ? 'border-destructive/50 bg-destructive/5' : ''}>
              <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  {globalUnsubscribe ? (
                    <BellOff className="mt-0.5 h-5 w-5 text-destructive" />
                  ) : (
                    <Bell className="mt-0.5 h-5 w-5 text-primary" />
                  )}
                  <div>
                    <Label className="text-base font-semibold">
                      {globalUnsubscribe ? 'All Notifications Paused' : 'Notifications Active'}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {globalUnsubscribe
                        ? 'You will not receive any notifications. Toggle this off to re-enable them.'
                        : 'You are receiving notifications based on the preferences below.'}
                    </p>
                  </div>
                </div>

                <ToggleSwitch
                  id="global-unsubscribe"
                  checked={!globalUnsubscribe}
                  onChange={handleGlobalUnsubscribeToggle}
                  disabled={saving}
                />
              </CardContent>
            </Card>
          </motion.div>

          {/* Warning banner when globally unsubscribed */}
          <AnimatePresence>
            {globalUnsubscribe && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="mb-6 overflow-hidden"
              >
                <div className="flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950/30">
                  <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    All notifications are currently paused. The preferences below will take
                    effect again once you re-enable notifications.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Desktop grid ─────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="hidden sm:block"
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Channel Preferences</CardTitle>
                <CardDescription>
                  Toggle notifications for each category and delivery channel.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="pb-3 pr-4 text-left text-sm font-medium text-muted-foreground">
                          Category
                        </th>
                        {CHANNELS.map((ch) => {
                          const Icon = ch.icon;
                          return (
                            <th
                              key={ch.key}
                              className="pb-3 text-center text-sm font-medium text-muted-foreground"
                            >
                              <div className="flex flex-col items-center gap-1">
                                <Icon className="h-4 w-4" />
                                <span>{ch.label}</span>
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {CATEGORIES.map((cat, idx) => {
                        const CatIcon = cat.icon;
                        return (
                          <motion.tr
                            key={cat.key}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.2, delay: 0.05 * idx }}
                            className="border-b last:border-b-0"
                          >
                            <td className="py-4 pr-4">
                              <div className="flex items-center gap-3">
                                <CatIcon className="h-5 w-5 text-muted-foreground" />
                                <div>
                                  <p className="font-medium text-foreground">{cat.label}</p>
                                  <p className="text-xs text-muted-foreground">{cat.description}</p>
                                </div>
                              </div>
                            </td>
                            {CHANNELS.map((ch) => {
                              const isEnabled =
                                preferences[ch.key]?.[cat.key] ?? cat.key !== 'MARKETING';
                              return (
                                <td key={ch.key} className="py-4 text-center">
                                  <div className="flex justify-center">
                                    <ToggleSwitch
                                      checked={isEnabled}
                                      onChange={() => togglePreference(ch.key, cat.key)}
                                      disabled={globalUnsubscribe || saving}
                                    />
                                  </div>
                                </td>
                              );
                            })}
                          </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* ── Mobile stacked cards ─────────────────────────────── */}
          <div className="flex flex-col gap-4 sm:hidden">
            {CATEGORIES.map((cat, idx) => {
              const CatIcon = cat.icon;
              return (
                <motion.div
                  key={cat.key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: 0.05 * idx }}
                >
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-3">
                        <CatIcon className="h-5 w-5 text-primary" />
                        <div>
                          <CardTitle className="text-base">{cat.label}</CardTitle>
                          <CardDescription className="text-xs">
                            {cat.description}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {CHANNELS.map((ch) => {
                        const Icon = ch.icon;
                        const isEnabled =
                          preferences[ch.key]?.[cat.key] ?? cat.key !== 'MARKETING';
                        return (
                          <div
                            key={ch.key}
                            className="flex items-center justify-between"
                          >
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4 text-muted-foreground" />
                              <Label className="text-sm">{ch.label}</Label>
                            </div>
                            <ToggleSwitch
                              checked={isEnabled}
                              onChange={() => togglePreference(ch.key, cat.key)}
                              disabled={globalUnsubscribe || saving}
                            />
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Save button */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.3 }}
            className="mt-8 flex justify-end"
          >
            <Button
              onClick={handleSave}
              disabled={!dirty || saving || globalUnsubscribe}
              className="min-w-[180px]"
            >
              {saving ? (
                <>
                  <BrandedSpinner className="mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Preferences
                </>
              )}
            </Button>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NotificationPreferencesPage;
